import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Award, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const FQCEngine = ({ user, onLogout }) => {
  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sampleResults, setSampleResults] = useState(null);

  useEffect(() => {
    loadConfiguration();
    loadSampleResults();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/iqc/templates/Android`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplate(response.data);
      setCategories(response.data.categories || []);
      
      if (response.data.categories && response.data.categories.length > 0) {
        setSelectedCategory(response.data.categories[0]);
        setParameters(response.data.categories[0].parameters || []);
      }
    } catch (error) {
      console.error('Error loading FQC template:', error);
      alert('Failed to load FQC template. Using IQC template.');
    } finally {
      setLoading(false);
    }
  };

  const loadSampleResults = () => {
    // Mock FQC results for demonstration
    const mockResults = {
      device_id: 'DEV-12345',
      qc_type: 'FQC',
      results: {
        severity_breakdown: {
          NTF: 40,
          Low: 5,
          Medium: 3,
          High: 1,
          Critical: 0
        },
        score: 88,
        grade: 'A-',
        parts_used: ['Display', 'Battery'],
        repair_quality: 'Excellent',
        pass_fqc: true
      }
    };
    setSampleResults(mockResults);
  };

  const calculateGrade = (severityBreakdown) => {
    const { NTF, Low, Medium, High, Critical } = severityBreakdown;
    
    // Auto E grade for Critical
    if (Critical > 0) {
      return { grade: 'E', score: 0, color: 'bg-red-600' };
    }
    
    // Calculate score
    let score = 100;
    score -= (Low * 1);
    score -= (Medium * 3);
    score -= (High * 5);
    
    // Determine grade
    let grade, color;
    if (score >= 95) {
      grade = 'A+';
      color = 'bg-green-600';
    } else if (score >= 90) {
      grade = 'A';
      color = 'bg-green-500';
    } else if (score >= 85) {
      grade = 'A-';
      color = 'bg-green-400';
    } else if (score >= 80) {
      grade = 'B+';
      color = 'bg-blue-500';
    } else if (score >= 75) {
      grade = 'B';
      color = 'bg-blue-400';
    } else if (score >= 70) {
      grade = 'B-';
      color = 'bg-blue-300';
    } else if (score >= 65) {
      grade = 'C+';
      color = 'bg-yellow-500';
    } else if (score >= 60) {
      grade = 'C';
      color = 'bg-yellow-400';
    } else if (score >= 55) {
      grade = 'C-';
      color = 'bg-yellow-300';
    } else if (score >= 40) {
      grade = 'D';
      color = 'bg-orange-500';
    } else {
      grade = 'E';
      color = 'bg-red-600';
    }
    
    return { grade, score, color };
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      'NTF': '✅',
      'Low': '🟡',
      'Medium': '🟠',
      'High': '🔴',
      'Critical': '🚨'
    };
    return icons[severity] || '•';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'NTF': 'bg-green-100 text-green-800 border-green-300',
      'Low': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Medium': 'bg-orange-100 text-orange-800 border-orange-300',
      'High': 'bg-red-100 text-red-800 border-red-300',
      'Critical': 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading FQC Configuration...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const gradeResult = sampleResults ? calculateGrade(sampleResults.results.severity_breakdown) : null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Award className="w-8 h-8 mr-3" />
            FQC Engine - Final Grade Assessment
          </h1>
          <p className="text-gray-600 mt-1">
            Post-repair quality check using same parameters as IQC • Calculates final grade based on severity
          </p>
        </div>

        {/* Grading Formula Info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-3" style={{ color: ZOBOX_BLUE }}>
            📊 FQC Grading Formula
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Scoring System:</p>
              <div className="space-y-1 text-sm">
                <p>• Start with 100 points</p>
                <p>• Low severity: -1 point each</p>
                <p>• Medium severity: -3 points each</p>
                <p>• High severity: -5 points each</p>
                <p>• Critical: Auto E grade</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Grade Ranges:</p>
              <div className="space-y-1 text-sm">
                <p>🟢 A+ (95-100), A (90-94), A- (85-89)</p>
                <p>🔵 B+ (80-84), B (75-79), B- (70-74)</p>
                <p>🟡 C+ (65-69), C (60-64), C- (55-59)</p>
                <p>🟠 D (40-54) | 🔴 E (0-39 or Critical)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sample FQC Result */}
        {sampleResults && gradeResult && (
          <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>
                Sample FQC Result - Device {sampleResults.device_id}
              </h3>
              <div className={`px-6 py-3 rounded-lg text-white ${gradeResult.color}`}>
                <div className="text-sm font-semibold">Final Grade</div>
                <div className="text-3xl font-bold">{gradeResult.grade}</div>
                <div className="text-xs">Score: {gradeResult.score}/100</div>
              </div>
            </div>

            {/* Severity Breakdown */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-700 mb-3">Severity Breakdown:</h4>
              <div className="grid grid-cols-5 gap-3">
                {Object.entries(sampleResults.results.severity_breakdown).map(([severity, count]) => (
                  <div key={severity} className={`p-4 rounded-lg border-2 ${getSeverityColor(severity)}`}>
                    <div className="text-2xl mb-1">{getSeverityIcon(severity)}</div>
                    <div className="text-xs font-semibold uppercase">{severity}</div>
                    <div className="text-2xl font-bold">{count}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Parts Used in Repair</p>
                <div className="flex flex-wrap gap-1">
                  {sampleResults.results.parts_used.map((part, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-200 text-blue-800 text-xs rounded">
                      {part}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Repair Quality</p>
                <p className="text-lg font-bold text-green-700">{sampleResults.results.repair_quality}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">FQC Status</p>
                <p className="text-lg font-bold text-purple-700">
                  {sampleResults.results.pass_fqc ? '✅ PASS' : '❌ FAIL'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* QC Categories - Same as IQC */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            FQC Uses Same QC Categories as IQC ({categories.length} Categories)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categories.map((category) => (
              <button
                key={category.category_id}
                onClick={() => {
                  setSelectedCategory(category);
                  setParameters(category.parameters || []);
                }}
                className={`p-4 rounded-lg font-medium transition-all border-2 ${
                  selectedCategory?.category_id === category.category_id
                    ? 'text-white border-blue-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                }`}
                style={selectedCategory?.category_id === category.category_id ? { backgroundColor: ZOBOX_BLUE } : {}}
              >
                <div className="text-2xl mb-1">{category.icon}</div>
                <div className="text-sm font-semibold">{category.name}</div>
                <div className="text-xs mt-1 opacity-75">
                  {category.parameters?.length || 0} params
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Category Parameters */}
        {selectedCategory && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center" style={{ color: ZOBOX_BLUE }}>
              <span className="text-3xl mr-3">{selectedCategory.icon}</span>
              {selectedCategory.name} - {parameters.length} Parameters
            </h3>
            
            <div className="space-y-4">
              {parameters.map((param) => (
                <div key={param.parameter_id} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="font-semibold text-gray-800 mb-2">
                    {param.order}. {param.name}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {param.options.map((option, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${getSeverityColor(option.severity)}`}
                      >
                        {getSeverityIcon(option.severity)} {option.value}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grade Reference Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            📋 FQC Grade Reference
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { grade: 'A+', range: '95-100', color: 'bg-green-600' },
              { grade: 'A', range: '90-94', color: 'bg-green-500' },
              { grade: 'A-', range: '85-89', color: 'bg-green-400' },
              { grade: 'B+', range: '80-84', color: 'bg-blue-500' },
              { grade: 'B', range: '75-79', color: 'bg-blue-400' },
              { grade: 'B-', range: '70-74', color: 'bg-blue-300' },
              { grade: 'C+', range: '65-69', color: 'bg-yellow-500' },
              { grade: 'C', range: '60-64', color: 'bg-yellow-400' },
              { grade: 'C-', range: '55-59', color: 'bg-yellow-300' },
              { grade: 'D', range: '40-54', color: 'bg-orange-500' },
              { grade: 'E', range: '0-39', color: 'bg-red-600' }
            ].map((item) => (
              <div key={item.grade} className={`p-3 rounded-lg text-white text-center ${item.color}`}>
                <div className="text-xl font-bold">{item.grade}</div>
                <div className="text-xs">{item.range}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">FQC Engine Information</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p>• FQC uses the same 11 categories and 49 parameters as IQC</p>
                <p>• After repair, engineer performs FQC to validate repair quality</p>
                <p>• Final grade is calculated based on remaining issues (severity levels)</p>
                <p>• Grade determines resale value and customer delivery readiness</p>
                <p>• Devices with Critical issues get automatic E grade and may need rework</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FQCEngine;
